import React, { useState, useRef } from 'react';
import { bulkCreateInvoices, validateInvoiceNumberExists } from '../../firebase/invoiceService';
import { auth } from '../../firebase/firebaseConfig';
import './CSVInvoiceImport.css';

/**
 * CSVInvoiceImport Component
 * Allows bulk import of historical invoices via CSV file upload
 * Supports historical import mode with date preservation and amount validation
 */
const CSVInvoiceImport = ({ onClose, onSuccess }) => {
  const [file, setFile] = useState(null);
  const [csvData, setCsvData] = useState([]);
  const [validationResults, setValidationResults] = useState([]);
  const [processing, setProcessing] = useState(false);
  const [importing, setImporting] = useState(false);
  const [results, setResults] = useState(null);
  const [step, setStep] = useState('upload'); // upload, validate, import, complete
  const [progress, setProgress] = useState(0);
  const fileInputRef = useRef(null);

  // Handle file selection
  const handleFileSelect = (event) => {
    const selectedFile = event.target.files[0];
    if (selectedFile && selectedFile.type === 'text/csv') {
      setFile(selectedFile);
      parseCSV(selectedFile);
    } else {
      alert('Please select a valid CSV file');
    }
  };

  // Parse CSV file
  const parseCSV = (file) => {
    setProcessing(true);
    const reader = new FileReader();
    
    reader.onload = (e) => {
      try {
        const text = e.target.result;
        
        // Split into lines, but handle multi-line quoted fields
        const lines = [];
        let currentLine = '';
        let inQuotes = false;
        
        for (let i = 0; i < text.length; i++) {
          const char = text[i];
          const nextChar = text[i + 1];
          
          if (char === '"') {
            inQuotes = !inQuotes;
            currentLine += char;
          } else if (char === '\n' && !inQuotes) {
            if (currentLine.trim()) {
              lines.push(currentLine);
            }
            currentLine = '';
          } else if (char === '\r' && nextChar === '\n' && !inQuotes) {
            if (currentLine.trim()) {
              lines.push(currentLine);
            }
            currentLine = '';
            i++; // Skip the \n
          } else {
            currentLine += char;
          }
        }
        // Add last line
        if (currentLine.trim()) {
          lines.push(currentLine);
        }
        
        if (lines.length < 2) {
          throw new Error('CSV file must contain at least a header row and one data row');
        }

        // Parse header
        const headers = parseCSVLine(lines[0], null).map(h => h.trim());
        const requiredHeaders = [
          'invoiceNumber', 'invoiceDate', 'customerName', 'attn', 'contactPhone',
          'invoiceType', 'lineItemsJSON', 'subtotal', 'grandTotal', 'submitToAccounting',
          'discountValue', 'discountAmount', 'discountType', 'gstPercentage', 'gstAmount'
        ];
        const missingHeaders = requiredHeaders.filter(h => !headers.includes(h));
        
        if (missingHeaders.length > 0) {
          throw new Error(`Missing required columns: ${missingHeaders.join(', ')}`);
        }

        // Parse data rows
        const data = [];
        for (let i = 1; i < lines.length; i++) {
          const line = lines[i];
          if (!line.trim()) continue;
          
          const row = parseCSVLine(line, headers);
          row.lineNumber = i + 1;
          data.push(row);
        }

        setCsvData(data);
        setStep('validate');
        validateData(data);
      } catch (error) {
        alert(`Error parsing CSV: ${error.message}`);
        setFile(null);
      } finally {
        setProcessing(false);
      }
    };

    reader.readAsText(file);
  };

  // Parse CSV line handling quoted fields with commas and escaped quotes
  const parseCSVLine = (line, headers) => {
    const values = [];
    let current = '';
    let inQuotes = false;
    
    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      const nextChar = line[i + 1];
      
      if (char === '"') {
        if (inQuotes && nextChar === '"') {
          // Doubled quote inside quoted field - this is an escaped quote
          current += '"';
          i++; // Skip the next quote
        } else {
          // Toggle quote state (entering or leaving quoted field)
          inQuotes = !inQuotes;
        }
      } else if (char === ',' && !inQuotes) {
        values.push(current);
        current = '';
      } else {
        current += char;
      }
    }
    values.push(current);
    
    // If no headers provided, return the array (for header parsing)
    if (!headers) {
      return values.map(v => v.trim());
    }
    
    // Otherwise, return object mapped to headers (for data row parsing)
    const row = {};
    headers.forEach((header, index) => {
      row[header] = values[index] || '';
    });
    
    return row;
  };

  // Convert CSV dates to unified date system
  // This function maps CSV fields to the unified date format expected by InvoiceList/InvoiceForm
  const convertToUnifiedDates = (row, invoiceType) => {
    const dateEntry = {
      id: 1
    };
    
    // Rental types: Use 'range' type with value/endValue for start/end dates
    if ((invoiceType === 'rental' || invoiceType === 'rental_extension') && row.rentalStartDate && row.rentalEndDate) {
      dateEntry.type = 'range';
      dateEntry.value = row.rentalStartDate;
      dateEntry.endValue = row.rentalEndDate;
    }
    // Service types with date range
    else if (invoiceType === 'scaffolding_service' && row.serviceStartDate && row.serviceEndDate) {
      dateEntry.type = 'range';
      dateEntry.value = row.serviceStartDate;
      dateEntry.endValue = row.serviceEndDate;
    }
    // Service types with single date
    else if (invoiceType === 'scaffolding_service' && row.serviceStartDate && !row.serviceEndDate) {
      dateEntry.type = 'date';
      dateEntry.value = row.serviceStartDate;
    }
    // Purchase types with lead time - support multiple formats
    else if (invoiceType === 'purchase') {
      // Lead time as specific date
      if (row.leadTimeInputType === 'date' && row.leadTime) {
        dateEntry.type = 'date';
        dateEntry.value = row.leadTime;
      }
      // Lead time as duration in months
      else if (row.leadTimeInputType === 'months' && row.leadTimeMonths) {
        dateEntry.type = 'months';
        dateEntry.leadTimeMonths = parseInt(row.leadTimeMonths);
      }
      // Lead time to be confirmed
      else if (row.leadTimeInputType === 'tbc') {
        dateEntry.type = 'tbc';
      }
      // Legacy support: if leadTime is provided without leadTimeInputType, assume date format
      else if (row.leadTime && !row.leadTimeInputType) {
        dateEntry.type = 'date';
        dateEntry.value = row.leadTime;
      }
    }
    // Default type from CSV if provided
    else if (row.dateInputType) {
      dateEntry.type = row.dateInputType;
    }
    // Fallback
    else {
      dateEntry.type = 'date';
    }
    
    // Add additional fields for duration-based rentals (if using duration instead of dates)
    if (row.rentalDuration) dateEntry.rentalDuration = parseInt(row.rentalDuration);
    if (row.rentalDurationUnit) dateEntry.rentalDurationUnit = row.rentalDurationUnit;
    if (row.rentalInputType) dateEntry.rentalInputType = row.rentalInputType;
    if (row.serviceInputType) dateEntry.serviceInputType = row.serviceInputType;
    
    return [dateEntry];
  };

  // Validate amount calculations
  const validateAmounts = (row) => {
    const subtotal = parseFloat(row.subtotal) || 0;
    const discountAmount = parseFloat(row.discountAmount) || 0;
    const gstAmount = parseFloat(row.gstAmount) || 0;
    const grandTotal = parseFloat(row.grandTotal) || 0;
    
    const calculated = subtotal - discountAmount + gstAmount;
    const difference = Math.abs(calculated - grandTotal);
    
    // Allow 0.01 tolerance for rounding
    if (difference > 0.01) {
      return {
        valid: false,
        error: `Amount mismatch in line ${row.lineNumber}: calculated ${calculated.toFixed(2)} but grandTotal is ${grandTotal.toFixed(2)} (difference: ${difference.toFixed(2)})`
      };
    }
    
    return { valid: true };
  };

  // Validate accounting field
  const validateAccountingField = (value) => {
    const normalizedValue = value.toLowerCase().trim();
    return ['yes', 'no'].includes(normalizedValue);
  };

  // Validate CSV data
  const validateData = async (data) => {
    setProcessing(true);
    
    try {
      const validationResults = [];
      
      for (const row of data) {
        const result = {
          lineNumber: row.lineNumber,
          data: row,
          errors: [],
          warnings: [],
          valid: true
        };

        // Validate invoice number format (BSIN-MMYYYY-XXX)
        const numberPattern = /^BSIN-\d{6}-\d{3}$/;
        if (!numberPattern.test(row.invoiceNumber)) {
          result.errors.push('Invoice number must be in format BSIN-MMYYYY-XXX');
          result.valid = false;
        } else {
          // Check for duplicates
          try {
            const exists = await validateInvoiceNumberExists(row.invoiceNumber);
            if (exists) {
              result.errors.push(`Invoice number ${row.invoiceNumber} already exists`);
              result.valid = false;
            }
          } catch (error) {
            result.warnings.push('Could not verify duplicate number');
          }
        }

        // Validate date format (YYYY-MM-DD)
        const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
        const dateFields = ['invoiceDate', 'rentalStartDate', 'rentalEndDate', 'serviceStartDate', 'serviceEndDate', 'leadTime'];
        
        dateFields.forEach(field => {
          if (field === 'invoiceDate') {
            // Invoice date is required
            if (!dateRegex.test(row[field])) {
              result.errors.push('invoiceDate must be in YYYY-MM-DD format');
              result.valid = false;
            } else {
              const date = new Date(row[field]);
              if (isNaN(date.getTime())) {
                result.errors.push('Invalid invoice date');
                result.valid = false;
              }
            }
          } else {
            // Optional date fields
            if (row[field] && !dateRegex.test(row[field])) {
              result.errors.push(`${field} must be in YYYY-MM-DD format if provided`);
              result.valid = false;
            }
          }
        });

        // Validate required fields
        if (!row.customerName) {
          result.errors.push('customerName is required');
          result.valid = false;
        }
        if (!row.attn) {
          result.errors.push('attn (Attention To) is required');
          result.valid = false;
        }
        if (!row.contactPhone) {
          result.errors.push('contactPhone is required');
          result.valid = false;
        }

        // Validate invoice type
        const validTypes = ['purchase', 'rental', 'rental_extension', 'scaffolding_service', 'damaged_items'];
        if (!validTypes.includes(row.invoiceType)) {
          result.errors.push(`Invalid invoice type. Must be one of: ${validTypes.join(', ')}`);
          result.valid = false;
        }

        // Validate status
        const validStatuses = ['draft', 'sent', 'paid', 'overdue', 'cancelled'];
        if (row.status && !validStatuses.includes(row.status.toLowerCase())) {
          result.errors.push(`Invalid status. Must be one of: ${validStatuses.join(', ')}`);
          result.valid = false;
        }

        // Validate submitToAccounting field
        if (!validateAccountingField(row.submitToAccounting)) {
          result.errors.push('submitToAccounting must be YES or NO');
          result.valid = false;
        }

        // Validate purchase invoice lead time fields
        if (row.invoiceType === 'purchase') {
          const validLeadTimeTypes = ['date', 'months', 'tbc'];
          
          if (row.leadTimeInputType) {
            // If leadTimeInputType is provided, validate it
            if (!validLeadTimeTypes.includes(row.leadTimeInputType)) {
              result.errors.push(`leadTimeInputType must be one of: ${validLeadTimeTypes.join(', ')}`);
              result.valid = false;
            } else {
              // Validate based on the type
              if (row.leadTimeInputType === 'date') {
                if (!row.leadTime) {
                  result.errors.push('leadTime is required when leadTimeInputType is "date"');
                  result.valid = false;
                } else if (!dateRegex.test(row.leadTime)) {
                  result.errors.push('leadTime must be in YYYY-MM-DD format when leadTimeInputType is "date"');
                  result.valid = false;
                }
              } else if (row.leadTimeInputType === 'months') {
                if (!row.leadTimeMonths) {
                  result.errors.push('leadTimeMonths is required when leadTimeInputType is "months"');
                  result.valid = false;
                } else if (isNaN(parseInt(row.leadTimeMonths)) || parseInt(row.leadTimeMonths) <= 0) {
                  result.errors.push('leadTimeMonths must be a positive number when leadTimeInputType is "months"');
                  result.valid = false;
                }
              }
              // 'tbc' type requires no additional fields
            }
          } else if (row.leadTime) {
            // Legacy support: if leadTime is provided without leadTimeInputType
            result.warnings.push('leadTimeInputType not specified, assuming "date" format for legacy support');
          } else {
            // No lead time information provided at all
            result.warnings.push('No lead time information provided (leadTimeInputType, leadTime, or leadTimeMonths)');
          }
        }

        // Validate line items JSON
        try {
          // Escape actual newlines/tabs to JSON-compatible format
          // This allows users to press Enter in CSV editors for multi-line descriptions
          const cleanedJSON = row.lineItemsJSON
            .replace(/\r\n/g, '\\n')  // Windows line endings
            .replace(/\n/g, '\\n')     // Unix line endings  
            .replace(/\r/g, '\\n')     // Mac line endings
            .replace(/\t/g, ' ');      // Replace tabs with spaces
          
          const lineItems = JSON.parse(cleanedJSON);
          if (!Array.isArray(lineItems) || lineItems.length === 0) {
            result.errors.push('lineItemsJSON must be a non-empty JSON array');
            result.valid = false;
          } else {
            // Validate line item structure
            lineItems.forEach((item, idx) => {
              if (!item.description) result.errors.push(`Line item ${idx + 1} missing description`);
              if (item.quantity === undefined) result.errors.push(`Line item ${idx + 1} missing quantity`);
              if (item.unitPrice === undefined) result.errors.push(`Line item ${idx + 1} missing unitPrice`);
              if (item.total === undefined) result.errors.push(`Line item ${idx + 1} missing total`);
            });
            if (result.errors.length > 0) {
              result.valid = false;
            }
          }
        } catch (error) {
          result.errors.push(`Invalid line items JSON format: ${error.message}`);
          result.valid = false;
        }

        // Validate numeric fields
        if (isNaN(parseFloat(row.subtotal))) {
          result.errors.push('subtotal must be a valid number');
          result.valid = false;
        }
        if (isNaN(parseFloat(row.grandTotal))) {
          result.errors.push('grandTotal must be a valid number');
          result.valid = false;
        }
        if (isNaN(parseFloat(row.discountAmount))) {
          result.errors.push('discountAmount must be a valid number');
          result.valid = false;
        }
        if (isNaN(parseFloat(row.gstAmount))) {
          result.errors.push('gstAmount must be a valid number');
          result.valid = false;
        }

        // Validate amount calculations
        if (result.valid) {
          const amountValidation = validateAmounts(row);
          if (!amountValidation.valid) {
            result.errors.push(amountValidation.error);
            result.valid = false;
          }
        }

        validationResults.push(result);
      }

      setValidationResults(validationResults);
    } catch (error) {
      alert(`Error validating data: ${error.message}`);
    } finally {
      setProcessing(false);
    }
  };

  // Import validated data
  const handleImport = async () => {
    const validRecords = validationResults.filter(r => r.valid);
    if (validRecords.length === 0) {
      alert('No valid records to import');
      return;
    }

    setImporting(true);
    setStep('import');
    setProgress(0);

    try {
      // Prepare data for bulk import with field mapping
      const invoicesToImport = validRecords.map(record => ({
        invoiceNumber: record.data.invoiceNumber,
        lineNumber: record.lineNumber,
        data: {
          // Map CSV fields to database fields
          companyName: record.data.customerName,
          attn: record.data.attn || record.data.customerName, // Use attn field, fallback to company name
          tel: record.data.contactPhone,
          email: record.data.contactEmail || '',
          billingAddress: record.data.customerAddress || '',
          siteAddress: record.data.siteAddress || '',
          
          invoiceType: record.data.invoiceType,
          invoiceDate: record.data.invoiceDate,
          status: (record.data.status || 'draft').toLowerCase(),
          
          // Unified dates system
          dates: convertToUnifiedDates(record.data, record.data.invoiceType),
          
          // Purchase invoice specific fields (lead time)
          ...(record.data.invoiceType === 'purchase' && {
            leadTime: record.data.leadTime || null,
            leadTimeInputType: record.data.leadTimeInputType || null,
            leadTimeMonths: record.data.leadTimeMonths ? parseInt(record.data.leadTimeMonths) : null,
          }),
          
          // Payment
          paymentTerms: record.data.paymentTerms || '50% deposit upon confirmation, balance on the first day of rental',
          poNumber: record.data.poNumber || '',
          
          // Line items - Escape actual newlines for JSON parsing
          // Mark all imported line items as manually edited to preserve descriptions
          lineItems: JSON.parse(
            record.data.lineItemsJSON
              .replace(/\r\n/g, '\\n')
              .replace(/\n/g, '\\n')
              .replace(/\r/g, '\\n')
              .replace(/\t/g, ' ')
          ).map(item => ({
            ...item,
            isManuallyEdited: true  // Prevent auto-description regeneration for historical data
          })),
          
          // Financial - PRESERVE EXACT VALUES FROM CSV
          subtotal: parseFloat(record.data.subtotal),
          discount: {
            type: record.data.discountType || 'none',
            value: parseFloat(record.data.discountValue) || 0
          },
          discountAmount: parseFloat(record.data.discountAmount) || 0, // PRESERVED
          gst: {
            percentage: parseFloat(record.data.gstPercentage) || 0
          },
          gstAmount: parseFloat(record.data.gstAmount) || 0, // PRESERVED
          grandTotal: parseFloat(record.data.grandTotal),
          
          // Accounting
          submitToAccounting: record.data.submitToAccounting.toLowerCase() === 'yes',
          
          // Notes
          notes: record.data.remarks || '',
          internalRemarks: record.data.internalRemarks || '',
          
          // References
          quotationId: record.data.quotationId || '',
          quotationNumber: record.data.quotationNumber || '',
          
          // Version
          version: parseInt(record.data.version) || 1,
          
          // Historical import metadata
          isHistoricalImport: true,
          importMetadata: {
            importedBy: auth.currentUser?.email || 'system',
            originalInvoiceDate: record.data.invoiceDate,
            importedAt: new Date().toISOString()
          }
        }
      }));

      // Call bulk import function
      const importResults = await bulkCreateInvoices(invoicesToImport);
      
      setResults(importResults);
      setStep('complete');

      if (onSuccess) {
        onSuccess(importResults);
      }

    } catch (error) {
      alert(`Import failed: ${error.message}`);
      setStep('validate');
    } finally {
      setImporting(false);
      setProgress(100);
    }
  };

  // Download sample CSV
  const downloadSample = () => {
    // Generate CSV content dynamically (matches the updated template with attn field)
    const csvContent = `invoiceNumber,invoiceDate,customerName,attn,contactPhone,contactEmail,siteAddress,customerAddress,invoiceType,status,rentalStartDate,rentalEndDate,serviceStartDate,serviceEndDate,leadTime,rentalDuration,rentalDurationUnit,dateInputType,rentalInputType,serviceInputType,paymentTerms,poNumber,quotationId,quotationNumber,lineItemsJSON,subtotal,discountValue,discountAmount,discountType,gstPercentage,gstAmount,grandTotal,submitToAccounting,internalRemarks,remarks,version
BSIN-092025-276,2025-09-09,FT2 Engineering Pte Ltd,Johnathon Tan,94889322,johnathon.tan@ft2.com.sg,10 Ubi Crescent #01-63A outside Lobby D Ubi Techpark,10 Ubi Crescent #01-63A,rental,paid,2025-09-09,2025-09-10,,,2025-09-09,,,date,date,date,50% deposit, balance on completion,,,,"[{""id"":1,""description"":""Single Width Aluminium Mobile Scaffold Dimensions: 2.00mL x 0.80mW x 5.00mH Platform Height: 4.00m Tower Height: 5.00m Max Working Height: 6.00m - 2 way transport (delivery and collection)"",""autoDescription"":""Single Width Aluminium Mobile Scaffold Dimensions: 2.00mL x 0.80mW x 5.00mH Platform Height: 4.00m Tower Height: 5.00m Max Working Height: 6.00m - 2 way transport (delivery and collection)"",""quantity"":1,""unitPrice"":440,""total"":440,""width"":""single"",""workingHeight"":6,""services"":{""assembly"":false,""endorsement"":false,""transportation"":true},""notes"":""""}]",440,0,0,none,0,0,440,YES,,Invoice paid in full - no outstanding balance,2
BSIN-012024-001,2024-01-15,ABC Construction Pte Ltd,John Lee,91234567,john@abc.com,123 Construction Site Blk 123,123 Business Park #01-01,purchase,paid,,,2024-01-18,,,,,date,,date,50% deposit, rest upon completion,,,,"[{""id"":1,""description"":""Single width scaffold 4m working height with transportation and assembly"",""quantity"":5,""unitPrice"":100.00,""total"":500.00,""width"":""single"",""workingHeight"":4}]",500.00,10,50.00,fixed,9,40.50,490.50,YES,High priority customer,Payment received in full,1
BSIN-022024-003,2024-02-05,Delta Engineering Pte Ltd,David Wong,87654321,david@delta.com,789 Project Site Avenue,789 Corporate Tower #03-03,rental_extension,paid,2024-02-01,2024-03-01,,,2024-02-01,1,months,date,date,,Payment due at end of extension period,,,,"[{""id"":1,""description"":""Rental extension for single width scaffold 5m"",""quantity"":2,""unitPrice"":80.00,""total"":160.00,""width"":""single"",""workingHeight"":5}]",160.00,10,16.00,percentage,9,12.96,156.96,YES,Extension invoiced and submitted,,1`;

    // Create blob and download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'invoices-import-template.csv';
    link.click();
    URL.revokeObjectURL(url);
  };

  const validCount = validationResults.filter(r => r.valid).length;
  const invalidCount = validationResults.filter(r => !r.valid).length;
  const accountingYesCount = validationResults.filter(r => 
    r.valid && r.data.submitToAccounting?.toLowerCase() === 'yes'
  ).length;

  return (
    <div className="csv-import-modal">
      <div className="csv-import-container">
        <div className="import-header">
          <h3>📊 CSV Invoice Import (Historical Mode)</h3>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>

        {/* Step 1: Upload */}
        {step === 'upload' && (
          <div className="import-step">
            <div className="step-content">
              <h4>Step 1: Upload CSV File</h4>
              <p>Upload a CSV file with historical invoice data. All amounts will be preserved exactly as provided (no recalculation).</p>
              
              <div className="file-upload-area">
                <input
                  type="file"
                  accept=".csv"
                  onChange={handleFileSelect}
                  ref={fileInputRef}
                  style={{ display: 'none' }}
                />
                <button 
                  className="upload-btn"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={processing}
                >
                  {processing ? '⏳ Processing...' : '📁 Select CSV File'}
                </button>
                {file && <p className="file-name">Selected: {file.name}</p>}
              </div>

              <div className="sample-download">
                <button className="sample-btn" onClick={downloadSample}>
                  📥 Download Sample CSV Template
                </button>
              </div>

              <div className="import-info" style={{ marginTop: 16, padding: 12, backgroundColor: '#f0f5ff', borderRadius: 4 }}>
                <h5>📋 CSV Format Requirements:</h5>
                <ul style={{ fontSize: '12px', margin: 0 }}>
                  <li><strong>invoiceNumber:</strong> BSIN-MMYYYY-XXX (e.g., BSIN-012024-001)</li>
                  <li><strong>invoiceDate:</strong> YYYY-MM-DD (e.g., 2024-01-15)</li>
                  <li><strong>customerName, contactPhone:</strong> Required</li>
                  <li><strong>discountAmount, gstAmount:</strong> Exact amounts (preserved, not recalculated)</li>
                  <li><strong>grandTotal:</strong> Must equal: subtotal - discountAmount + gstAmount</li>
                  <li><strong>submitToAccounting:</strong> YES or NO</li>
                </ul>
              </div>
            </div>
          </div>
        )}

        {/* Step 2: Validate */}
        {step === 'validate' && (
          <div className="import-step">
            <div className="step-content">
              <h4>Step 2: Validation Results</h4>
              <div className="validation-summary">
                <div className="summary-item valid">
                  ✅ Valid Records: {validCount}
                </div>
                <div className="summary-item invalid">
                  ❌ Invalid Records: {invalidCount}
                </div>
                {validCount > 0 && (
                  <div className="summary-item accounting">
                    💼 Submit to Accounting: {accountingYesCount}
                  </div>
                )}
              </div>

              <div className="validation-details">
                {validationResults.map((result, index) => (
                  <div key={index} className={`validation-row ${result.valid ? 'valid' : 'invalid'}`}>
                    <div className="row-header">
                      <span className="line-number">Line {result.lineNumber}</span>
                      <span className="invoice-number">{result.data.invoiceNumber}</span>
                      <span className="company-name">{result.data.customerName}</span>
                      <span className="date">{result.data.invoiceDate}</span>
                      <span className="amount">${parseFloat(result.data.grandTotal || 0).toFixed(2)}</span>
                      {result.valid && result.data.submitToAccounting && (
                        <span className={`accounting-badge ${result.data.submitToAccounting.toLowerCase()}`}>
                          {result.data.submitToAccounting.toUpperCase()}
                        </span>
                      )}
                      {result.valid && (
                        <span className="historical-badge">📜 HISTORICAL</span>
                      )}
                    </div>
                    {result.errors.length > 0 && (
                      <div className="errors">
                        {result.errors.map((error, i) => (
                          <div key={i} className="error">❌ {error}</div>
                        ))}
                      </div>
                    )}
                    {result.warnings.length > 0 && (
                      <div className="warnings">
                        {result.warnings.map((warning, i) => (
                          <div key={i} className="warning">⚠️ {warning}</div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>

              <div className="step-actions">
                <button className="btn-secondary" onClick={() => setStep('upload')}>
                  ← Back
                </button>
                <button 
                  className="btn-primary"
                  onClick={handleImport}
                  disabled={validCount === 0}
                >
                  Import {validCount} Historical Records →
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Step 3: Import Progress */}
        {step === 'import' && (
          <div className="import-step">
            <div className="step-content">
              <h4>Step 3: Importing Historical Records</h4>
              <div className="import-progress">
                <div className="progress-spinner"></div>
                <p>Importing historical invoices to Firebase...</p>
                <p>Preserving exact dates and amounts from CSV...</p>
                <p>Please wait, this may take a few moments.</p>
              </div>
            </div>
          </div>
        )}

        {/* Step 4: Complete */}
        {step === 'complete' && results && (
          <div className="import-step">
            <div className="step-content">
              <h4>Step 4: Import Complete</h4>
              <div className="import-results">
                <div className="result-summary">
                  <div className="result-item success">
                    ✅ Successfully imported: {results.successful} historical invoices
                  </div>
                  {results.failed > 0 && (
                    <div className="result-item error">
                      ❌ Failed to import: {results.failed} invoices
                    </div>
                  )}
                </div>

                {results.errors.length > 0 && (
                  <div className="error-details">
                    <h5>Import Errors:</h5>
                    {results.errors.map((error, index) => (
                      <div key={index} className="error-item">
                        Line {error.line} ({error.invoiceNumber}): {error.error}
                      </div>
                    ))}
                  </div>
                )}

                <div className="next-steps" style={{ marginTop: 16, padding: 12, backgroundColor: '#f6ffed', borderRadius: 4 }}>
                  <h5>✅ Historical Import Complete - Next Steps:</h5>
                  <ul style={{ fontSize: '12px', margin: 0 }}>
                    <li>All invoices marked with <strong>isHistoricalImport: true</strong></li>
                    <li>Original invoice numbers and dates preserved</li>
                    <li>All amounts imported exactly as provided (no recalculation)</li>
                    <li>Form fields will be <strong>read-only</strong> when viewing these invoices</li>
                    <li>Internal remarks remain editable for admin notes</li>
                    <li>PDF generation disabled - use manual upload only</li>
                    <li>Historical invoices appear in analytics for their original date periods</li>
                  </ul>
                </div>
              </div>

              <div className="step-actions">
                <button className="btn-primary" onClick={onClose}>
                  Done
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Help Section */}
        <div className="import-help">
          <h5>💡 Historical Import Features:</h5>
          <ul>
            <li><strong>Date Preservation:</strong> Invoice dates stored exactly as provided</li>
            <li><strong>Amount Validation:</strong> Strict validation ensures subtotal - discount + GST = total</li>
            <li><strong>Read-Only Display:</strong> Historical invoices shown as read-only in forms</li>
            <li><strong>Analytics Integration:</strong> Historical data appears in correct time periods</li>
            <li><strong>PDF Upload Only:</strong> No auto-generation for historical invoices</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default CSVInvoiceImport;
